import styled, { keyframes } from 'styled-components'
import { SPACING, COLORS, TYPOGRAPHY, Z_INDEX, MEDIA } from '../../../../styles/theme'

/**
 * MindMapHeader styled components
 * Refactored from inline styles to styled-components
 */

// Keyframe animations
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(10px); }
  to { opacity: 1; transform: translateX(0); }
`

// Header container - spans full area with zoom at bottom-left, buttons on right
export const HeaderButtonsContainer = styled.div`
  position: absolute;
  top: ${SPACING.lg};
  left: ${SPACING.lg};
  right: ${SPACING.lg};
  bottom: ${SPACING.lg};
  z-index: ${Z_INDEX.dropdown};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  ${MEDIA.mobile} {
    z-index: 10;
    top: ${SPACING.md};
    left: ${SPACING.md};
    right: ${SPACING.md};
    bottom: ${SPACING.md};
  }
`

// Left side controls group (zoom at bottom, search at top)
export const LeftControlsGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: 100%;
  gap: ${SPACING.sm};
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  /* ZoomSlider is first child - move to bottom */
  > *:first-child {
    order: 1;
    margin-top: auto;
  }
`

// Right side buttons group
export const HeaderButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};

  ${MEDIA.mobile} {
    flex-direction: column;
    align-items: flex-end;
    align-self: stretch;
    gap: ${SPACING.xs};
  }
`

// Save status indicator
export const SaveStatusIndicator = styled.div<{ $isSaving: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: ${COLORS.gray400};
  animation: ${fadeIn} 0.2s ease;
  order: -1;
`

// Spinning loader icon
export const SpinningLoader = styled.div`
  animation: ${spin} 1s linear infinite;
  display: flex;
  align-items: center;
`

// Styled button wrapper for Ant Design Button with consistent shadow
export const StyledButtonWrapper = styled.div`
  & .ant-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 ${SPACING.md};
    box-shadow: var(--shadow-dropdown);
  }

  & .ant-btn-icon-only {
    width: auto;
    padding: 0 ${SPACING.md};
  }

  & .ant-btn-default {
    border: 1px solid var(--color-border-dark);
    background: var(--color-bg-elevated);

    &:hover {
      background: var(--color-bg-hover);
      border-color: var(--color-border-dark);
    }
  }

  & .ant-btn-primary {
    border: none;
  }
`

// On mobile: horizontal row at the top of the button column (save indicator + settings side by side).
// On desktop: display:contents makes it transparent so children flow as normal flex items.
export const MobileTopRow = styled.div`
  display: contents;

  ${MEDIA.mobile} {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${SPACING.sm};
    order: -1;
  }
`

// Button text that hides on mobile (icon-only buttons)
export const ButtonText = styled.span`
  ${MEDIA.mobile} {
    display: none;
  }
`

// Save status text that hides on mobile
export const SaveStatusText = styled.span`
  ${MEDIA.mobile} {
    display: none;
  }
`

// ── Research split button ────────────────────────────────────────────────────
// A two-part button: "Start research round" (primary CTA) on the left, and a
// flask + count chevron toggle on the right that opens the research history.

const RESEARCH_GREEN = '#16a34a'
const RESEARCH_GREEN_HOVER = '#15803d'
const RESEARCH_GREEN_ACTIVE = '#166534'
// Translucent overlays so the muted pill reads as a raised surface in both
// light and dark themes (rather than blending into the canvas).
const RESEARCH_MUTED_BG = 'rgba(127, 127, 127, 0.18)'
const RESEARCH_MUTED_BG_HOVER = 'rgba(127, 127, 127, 0.28)'
const RESEARCH_MUTED_DIVIDER = 'rgba(127, 127, 127, 0.35)'
const RESEARCH_MUTED_TEXT = 'var(--color-text-muted, rgba(255, 255, 255, 0.6))'

export const ResearchSplitWrapper = styled.div<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: stretch;
  border-radius: 22px;
  border: ${({ $disabled }) =>
    $disabled ? '1px solid var(--color-border-dark)' : '1px solid transparent'};
  background: ${({ $disabled }) => ($disabled ? 'var(--color-bg-elevated)' : 'transparent')};
  box-shadow: ${({ $disabled }) =>
    $disabled ? 'var(--shadow-dropdown)' : '0 4px 16px rgba(22, 163, 74, 0.35)'};
  isolation: isolate;
  transition:
    box-shadow 0.2s ease,
    background 0.2s ease;
`

const splitButtonBase = `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
`

// Pass-through wrapper used when the start button is disabled — Antd Tooltip
// needs a non-disabled element to bind hover, so we wrap the disabled button.
export const StartResearchSlot = styled.span`
  display: inline-flex;
  align-items: stretch;

  > button {
    flex: 1;
  }
`

export const StartResearchButton = styled.button<{ $attached?: boolean }>`
  ${splitButtonBase}
  padding: 0 ${SPACING.md};
  border-radius: ${({ $attached }) => ($attached ? '22px 0 0 22px' : '22px')};
  position: relative;
  color: #ffffff;
  background: ${RESEARCH_GREEN};
  box-shadow: ${({ $attached }) => ($attached ? 'none' : '0 4px 16px rgba(22, 163, 74, 0.35)')};

  &:hover:not(:disabled) {
    background: ${RESEARCH_GREEN_HOVER};
  }

  &:active:not(:disabled) {
    background: ${RESEARCH_GREEN_ACTIVE};
  }

  &:disabled {
    background: ${RESEARCH_MUTED_BG};
    color: ${RESEARCH_MUTED_TEXT};
    cursor: not-allowed;
    box-shadow: ${({ $attached }) => ($attached ? 'none' : 'var(--shadow-dropdown)')};
    border: ${({ $attached }) => ($attached ? 'none' : '1px solid var(--color-border-dark)')};
  }

  /* Divider before the history toggle — only relevant in the split-button form. */
  &::after {
    content: ${({ $attached }) => ($attached ? "''" : 'none')};
    position: absolute;
    top: 8px;
    bottom: 8px;
    right: 0;
    width: 1px;
    background: ${({ disabled }) =>
      disabled ? RESEARCH_MUTED_DIVIDER : 'rgba(255, 255, 255, 0.25)'};
  }
`

export const HistoryToggleButton = styled.button<{ $open: boolean; $muted?: boolean }>`
  ${splitButtonBase}
  padding: 0 ${SPACING.sm};
  border-radius: 0 22px 22px 0;

  ${({ $muted, $open }) =>
    $muted
      ? `
        color: ${RESEARCH_MUTED_TEXT};
        background: ${$open ? RESEARCH_MUTED_BG_HOVER : RESEARCH_MUTED_BG};

        &:hover {
          background: ${RESEARCH_MUTED_BG_HOVER};
          color: var(--color-text-primary);
        }
      `
      : `
        color: #ffffff;
        background: ${$open ? RESEARCH_GREEN_ACTIVE : RESEARCH_GREEN};

        &:hover {
          background: ${RESEARCH_GREEN_HOVER};
        }

        &:active {
          background: ${RESEARCH_GREEN_ACTIVE};
        }
      `}
`

export const HistoryCountBadge = styled.span<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: ${({ $muted }) => ($muted ? 'transparent' : 'rgba(0, 0, 0, 0.25)')};
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`

// ── Research history popover ────────────────────────────────────────────────

export const HistoryPopoverContainer = styled.div`
  width: 320px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-dark, rgba(255, 255, 255, 0.08));
  border-radius: 12px;
  box-shadow: var(--shadow-dropdown);
  overflow: hidden;
`

export const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 8px;
`

export const HistoryHeaderTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted, rgba(255, 255, 255, 0.45));
`

export const HistoryHeaderCount = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-muted, rgba(255, 255, 255, 0.45));
  font-variant-numeric: tabular-nums;
`

export const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 6px;
`

export const HistoryItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s ease;

  &:hover {
    background: var(--color-bg-hover);
  }
`

export const HistoryItemIcon = styled.span<{ $type: 'survey' | 'interview' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  color: ${({ $type }) => ($type === 'survey' ? '#22c55e' : '#a78bfa')};
  background: ${({ $type }) =>
    $type === 'survey' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(167, 139, 250, 0.14)'};
`

export const HistoryItemBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const HistoryItemLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const HistoryItemMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: var(--color-text-muted, rgba(255, 255, 255, 0.5));
`

export const HistoryItemArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted, rgba(255, 255, 255, 0.4));
  flex-shrink: 0;
`

export const HistoryFooter = styled.div`
  padding: 6px;
  border-top: 1px solid var(--color-border, rgba(255, 255, 255, 0.06));
`

export const HistoryFooterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px;
  text-align: left;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary, rgba(255, 255, 255, 0.7));
  transition: background 0.12s ease;

  &:hover:not(:disabled) {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const HistoryFooterArrow = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
`

export const EmptyHistory = styled.div`
  padding: 14px 14px 18px;
  text-align: left;
  font-size: 13px;
  line-height: 1.45;
  color: var(--color-text-muted, rgba(255, 255, 255, 0.5));
`
